import { useState } from 'react';
import { Modal, Button, NotificationDispatch, TextField } from '@tryhackme/thm-ui-components';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { StyledWrapper, StyledTitle, StyledContent, StyledText, StyledActionButtonsWrapper, StyledInput, StyledInputWrapper } from 'src/features/admin/manage-users/modals/modals.styles';
import { StyledFontAwesomeCheckIcon, StyledUsernameRegexCondition, StyledUsernameRegexConditionsWrapper, StyledRegexInvalidDot, StyledFontAwesomeCheckIconWrapper } from 'src/features/admin/manage-users/modals/change-username/change-username-modal.styles';
import { useRenameUserMutation } from 'src/features/admin/manage-users/manage-users.slice';
import { RenameUserData } from 'src/common/interfaces';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import { changeUsernameSymbolsRegex } from 'src/features/admin/manage-users/manage-users.constants';
import { useForm, Controller } from 'src/common/form';
import { DEFAULT_INPUT_VALUES, changeUsernameModalFormValidation } from 'src/features/admin/manage-users/modals/change-username/change-username-modal.constants';
import { ModalProps } from '../modal.types';
interface ModalChangeUsernameProps extends ModalProps {
  searchQuery?: string;
  setSearchQuery: (value: string) => void;
  currentValue: string;
}
export const ModalChangeUsername = ({
  isOpen,
  setIsOpen,
  userId,
  searchQuery,
  currentValue,
  setSearchQuery
}: ModalChangeUsernameProps) => {
  const [containsSymbols, setContainsSymbols] = useState<boolean>(true);
  const [tooMuchCharacters, setTooMuchCharacters] = useState<boolean>(true);
  const [changeUsernameData, {
    isLoading
  }] = useRenameUserMutation();
  const {
    setValue,
    register,
    handleSubmit,
    control,
    reset,
    getValues,
    formState: {
      isValid
    }
  } = useForm({
    resolver: yupResolver(changeUsernameModalFormValidation),
    mode: 'onChange',
    defaultValues: DEFAULT_INPUT_VALUES
  });
  const resetConditions = () => {
    setContainsSymbols(true);
    setTooMuchCharacters(true);
  };
  const closeModal = () => {
    resetConditions();
    reset();
    setIsOpen(!isOpen);
  };
  const onRenameUser = async () => {
    const {
      value: newUsername
    } = getValues();
    try {
      await changeUsernameData(({
        newUsername,
        userId
      } as RenameUserData)).unwrap();
      if (searchQuery === currentValue && newUsername) {
        setSearchQuery(newUsername);
      }
      NotificationDispatch('success', 'Username successfully changed');
      closeModal();
    } catch (error: unknown) {
      NotificationDispatch('error', isQueryErrorWithMessage(error) ? error.data.message : 'An unknown error has occurred');
    }
  };
  const handleChange = (value?: string) => {
    if (value) {
      setValue('value', value, {
        shouldValidate: true
      });
    }
  };
  return <Modal open={isOpen} onOpenChange={closeModal} isSmall data-sentry-element="Modal" data-sentry-component="ModalChangeUsername" data-sentry-source-file="change-username-modal.tsx">
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form onSubmit={handleSubmit(onRenameUser)}>
        <StyledWrapper data-sentry-element="StyledWrapper" data-sentry-source-file="change-username-modal.tsx">
          <StyledTitle data-sentry-element="StyledTitle" data-sentry-source-file="change-username-modal.tsx">Update user’s username</StyledTitle>
          <StyledContent data-sentry-element="StyledContent" data-sentry-source-file="change-username-modal.tsx">
            <StyledText data-sentry-element="StyledText" data-sentry-source-file="change-username-modal.tsx">What shall the user be called (case-sensitive)?</StyledText>
            <StyledInputWrapper data-sentry-element="StyledInputWrapper" data-sentry-source-file="change-username-modal.tsx">
              <StyledInput data-sentry-element="StyledInput" data-sentry-source-file="change-username-modal.tsx">
                <StyledText data-sentry-element="StyledText" data-sentry-source-file="change-username-modal.tsx">Username</StyledText>
                <Controller name="value" control={control} render={({
                field: {
                  ref,
                  value,
                  onChange,
                  name,
                  ...rest
                }
              }) => <TextField {...register('value')} id="change-username-modal" variant="input" type="text" placeholder="example" onChange={e => {
                handleChange(e.target.value);
                setTooMuchCharacters(!(e.target.value.length && e.target.value.length <= 16));
                setContainsSymbols(!(e.target.value.length && changeUsernameSymbolsRegex.test(e.target.value)));
              }} {...rest} />} data-sentry-element="Controller" data-sentry-source-file="change-username-modal.tsx" />
              </StyledInput>
              <StyledUsernameRegexConditionsWrapper data-sentry-element="StyledUsernameRegexConditionsWrapper" data-sentry-source-file="change-username-modal.tsx">
                <StyledUsernameRegexCondition data-sentry-element="StyledUsernameRegexCondition" data-sentry-source-file="change-username-modal.tsx">
                  {containsSymbols && <StyledRegexInvalidDot>•</StyledRegexInvalidDot>}
                  {!containsSymbols && <StyledFontAwesomeCheckIconWrapper>
                      <StyledFontAwesomeCheckIcon icon={faCircleCheck} />
                    </StyledFontAwesomeCheckIconWrapper>}
                  Username cannot contain any symbols, other than periods.
                </StyledUsernameRegexCondition>
                <StyledUsernameRegexCondition data-sentry-element="StyledUsernameRegexCondition" data-sentry-source-file="change-username-modal.tsx">
                  {tooMuchCharacters && <StyledRegexInvalidDot>•</StyledRegexInvalidDot>}
                  {!tooMuchCharacters && <StyledFontAwesomeCheckIconWrapper>
                      <StyledFontAwesomeCheckIcon icon={faCircleCheck} />
                    </StyledFontAwesomeCheckIconWrapper>}
                  Username cannot be longer than 16 characters.
                </StyledUsernameRegexCondition>
                <StyledUsernameRegexCondition data-sentry-element="StyledUsernameRegexCondition" data-sentry-source-file="change-username-modal.tsx">
                  <StyledRegexInvalidDot data-sentry-element="StyledRegexInvalidDot" data-sentry-source-file="change-username-modal.tsx">•</StyledRegexInvalidDot>
                  All usernames are case sensitive.
                </StyledUsernameRegexCondition>
              </StyledUsernameRegexConditionsWrapper>
            </StyledInputWrapper>
          </StyledContent>
          <StyledActionButtonsWrapper data-sentry-element="StyledActionButtonsWrapper" data-sentry-source-file="change-username-modal.tsx">
            <Button onClick={closeModal} variant="outlined" color="secondary" data-sentry-element="Button" data-sentry-source-file="change-username-modal.tsx">
              Cancel
            </Button>
            <Button
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onClick={onRenameUser} disabled={containsSymbols || tooMuchCharacters || !isValid || isLoading} isLoading={isLoading} variant="solid" color="primary" data-sentry-element="Button" data-sentry-source-file="change-username-modal.tsx">
              Update username
            </Button>
          </StyledActionButtonsWrapper>
        </StyledWrapper>
      </form>
    </Modal>;
};